import {
  AnchorHTMLAttributes,
  createContext,
  MouseEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type RouterValue = {
  navigate: (to: string, options?: { replace?: boolean }) => void
  pathname: string
}

const RouterContext = createContext<RouterValue | null>(null)

function currentPath() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [pathname, setPathname] = useState(currentPath)

  useEffect(() => {
    const onPopState = () => setPathname(currentPath())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    if (options?.replace) window.history.replaceState(null, '', to)
    else window.history.pushState(null, '', to)
    setPathname(currentPath())
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  const value = useMemo(() => ({ navigate, pathname: pathname.split(/[?#]/, 1)[0] || '/' }), [navigate, pathname])
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
}

function useRouter() {
  const router = useContext(RouterContext)
  if (!router) throw new Error('RouterProvider is missing')
  return router
}

export function useNavigate() {
  return useRouter().navigate
}

export function usePathname() {
  return useRouter().pathname
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }

export function Link({ children, onClick, target, to, ...props }: LinkProps) {
  const navigate = useNavigate()

  function follow(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event)
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || target === '_blank') return
    event.preventDefault()
    navigate(to)
  }

  return <a {...props} href={to} target={target} onClick={follow}>{children}</a>
}

export function NavLink({ className, end = false, to, ...props }: LinkProps & { end?: boolean }) {
  const pathname = usePathname()
  const active = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`)
  const resolvedClassName = [className, active ? 'active' : ''].filter(Boolean).join(' ')
  return <Link {...props} className={resolvedClassName || undefined} to={to} />
}

export function Navigate({ replace = false, to }: { replace?: boolean; to: string }) {
  const navigate = useNavigate()
  useEffect(() => navigate(to, { replace }), [navigate, replace, to])
  return null
}
